import { NoInfer } from "./utilityTypes";

export const isLastIndexOf = <T>(array: T[], index: number) => index === array.length - 1;

export const isElementInArray = <T>(array?: T[], element?: T) =>
  array && element && array.indexOf(element) >= 0;

type SortArrayByProps<T> = {
  values: T[];
  keyToSort: keyof T;
  direction?: "ascending" | "descending";
};

export const sortArrayBy = <T>({
  values,
  keyToSort,
  direction = "ascending",
}: SortArrayByProps<T>) => {
  const compare = (objectA: T, objectB: T) => {
    const valueA = objectA[keyToSort];
    const valueB = objectB[keyToSort];

    if (valueA === valueB) {
      return 0;
    }

    if (valueA > valueB) {
      return direction === "ascending" ? 1 : -1;
    }

    return direction === "ascending" ? -1 : 1;
  };

  return values.sort(compare);
};

export const filterByIds = <T extends { Id: number }>(objects: T[], ids: number[]): T[] =>
  objects.filter((obj) => ids.includes(obj.Id));

export const removeIndexFromArray = <T>(array: T[], indexToRemove: number) => [
  ...array.slice(0, indexToRemove),
  ...array.slice(indexToRemove + 1),
];

export const isArrayUndefinedOrEmpty = <T>(arr: T[] | undefined): arr is undefined | [] =>
  arr === undefined || arr.length === 0;

type FindByKeyProps<T> = {
  list: T[] | undefined;
  identifier: number | undefined;
  keyOfType: keyof T;
};
export const findByKey = <T>({ list, identifier, keyOfType }: FindByKeyProps<NoInfer<T>>) => {
  if (!list) return undefined;

  return list.find((item) => {
    return item[keyOfType] === identifier;
  });
};

type FindByKeys<T> = {
  list: T[] | undefined;
  identifier: number | undefined;
  firstKey: keyof T;
  secondKey: keyof T;
};
export const findByKeys = <T>({
  list,
  firstKey,
  identifier,
  secondKey,
}: FindByKeys<T>): T | undefined => {
  if (!list || !identifier) return undefined;

  return list.find((item) => {
    return item[firstKey] === identifier || item[secondKey] === identifier;
  });
};

export const mapKeyToArray = <T extends object, K extends keyof T>(items: T[], key: K) =>
  items.filter((item) => item[key] !== undefined && item[key] !== null).map((item) => item[key]);

export const combineStringArrays = (values: string[], ...arrays: string[][]): string[] => {
  return values.concat(...arrays);
};
